import { useState, useEffect, useCallback } from 'react';

export function useTreeExpansion(treeId?: string) {
    const storageKey = treeId ? `tree-control-expanded-${treeId}` : null;

    const [expandedIds, setExpandedIds] = useState<Set<number>>(() => {
        if (!storageKey) {
            return new Set();
        }

        try {
            const saved = localStorage.getItem(storageKey);
            if (!saved) {
                return new Set();
            }

            const ids = JSON.parse(saved);
            return Array.isArray(ids) ? new Set(ids) : new Set();
        } catch (error) {
            console.warn('Failed to load tree expansion state:', error);
            return new Set();
        }
    });

    useEffect(() => {
        if (!storageKey) {
            return;
        }

        try {
            localStorage.setItem(storageKey, JSON.stringify(Array.from(expandedIds)));
        } catch (error) {
            console.warn('Failed to save tree expansion state:', error);
        }
    }, [expandedIds, storageKey]);

    const toggleExpanded = useCallback((nodeId: number) => {
        setExpandedIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(nodeId)) {
                newSet.delete(nodeId);
            } else {
                newSet.add(nodeId);
            }
            return newSet;
        });
    }, []);

    const setExpanded = useCallback((nodeId: number, isExpanded: boolean) => {
        setExpandedIds(prev => {
            const newSet = new Set(prev);
            if (isExpanded) {
                newSet.add(nodeId);
            } else {
                newSet.delete(nodeId);
            }
            return newSet;
        });
    }, []);

    return {
        expandedIds,
        toggleExpanded,
        setExpanded,
        setExpandedIds
    };
}
